import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import type {
  CardExpirationDateElement,
  CardNumberElement,
  CardVerificationCodeElement,
} from '@basis-theory/basis-theory-js/types/elements';
import { resolveBasisTheoryClient, type BasisTheoryClient } from 'services/basisTheory';
import errorMonitoring from '@/errorMonitoring';

interface CardElementPlaceholders {
  cardNumber: string;
  expiration: string;
  cvc: string;
}

interface CardElementAriaLabels {
  cardNumber: string;
  expiration: string;
  cvc: string;
}

interface BasisTheoryCardElements {
  cardNumber: CardNumberElement;
  cardExpiration: CardExpirationDateElement;
  cardCvc: CardVerificationCodeElement;
}

interface BasisTheoryCardDetails {
  bin: string;
  brand: string;
  expirationComplete: boolean;
  last4: string;
}

interface CardNumberUpdate {
  cardBin?: string;
  cardBrand?: string;
  cardLast4?: string;
  complete?: boolean;
  empty?: boolean;
}

interface ElementValidityUpdate {
  complete?: boolean;
  empty?: boolean;
  valid?: boolean;
}

const CARD_BIN_LENGTH = 6;

const ELEMENT_STYLE = {
  base: {
    'fontFamily': 'inherit',
    'fontSize': '16px',
    'lineHeight': '36px',
    'color': '#32325d',
    '::placeholder': { color: '#767676' },
  },
};

/** Normalize Basis Theory metadata to the six digits required by BIN validation. */
function normalizeCardBin(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '').slice(0, CARD_BIN_LENGTH);
}

/** Unmount every created secure card element without allowing one failure to skip the rest. */
function safeUnmountCardElements(elements: Partial<BasisTheoryCardElements> | null): void {
  Object.values(elements ?? {}).forEach((element) => {
    try {
      element.unmount();
    } catch (error) {
      errorMonitoring.notify({ error, info: { stage: 'new_card_unmount' } });
    }
  });
}

type FocusableSecureElement = Pick<CardNumberElement, 'focus'>;

const SECURE_FOCUS_FALLBACK_DELAY_MS = 250;

/**
 * Chrome DevTools Device Toolbar can focus a Basis Theory iframe without focusing its inner
 * secure input, which makes the card fields appear active but prevents typing. Keep the iframe's
 * native gesture intact so real browsers can offer payment autofill, then use the SDK focus only
 * as a delayed fallback when Basis Theory does not confirm that its input received focus.
 */
function bridgeSecureElementFocus(
  targetId: string,
  element: FocusableSecureElement,
  hasSecureInputFocus: () => boolean,
): () => void {
  const iframe = document.querySelector(`#${targetId} iframe`);
  if (!(iframe instanceof HTMLIFrameElement)) return () => undefined;
  let fallbackTimer: number | undefined;

  function clearFallback() {
    if (fallbackTimer === undefined) return;
    window.clearTimeout(fallbackTimer);
    fallbackTimer = undefined;
  }

  function scheduleFallback() {
    clearFallback();
    fallbackTimer = window.setTimeout(() => {
      fallbackTimer = undefined;
      if (!hasSecureInputFocus()) element.focus();
    }, SECURE_FOCUS_FALLBACK_DELAY_MS);
  }

  iframe.addEventListener('focus', scheduleFallback);

  return () => {
    iframe.removeEventListener('focus', scheduleFallback);
    clearFallback();
  };
}

type BasisTheoryElementsStatus = 'error' | 'loading' | 'ready';

interface UseBasisTheoryElementsOptions {
  ariaLabels: CardElementAriaLabels;
  publicKey: string;
  placeholders: CardElementPlaceholders;
  onCardBrandChange: (cardBrand: string) => void;
  onCardDetailsChange?: (details: BasisTheoryCardDetails) => void;
  retryKey?: number;
}

interface UseBasisTheoryElementsResult {
  clientRef: MutableRefObject<BasisTheoryClient | null>;
  elementsRef: MutableRefObject<BasisTheoryCardElements | null>;
  isComplete: boolean;
  mountedRef: MutableRefObject<boolean>;
  status: BasisTheoryElementsStatus;
}

/**
 * Mount and own the lifecycle of the Basis Theory secure card elements.
 *
 * @param {object} options - Hook options.
 * @param {string} options.publicKey - Basis Theory public API key.
 * @param {object} options.placeholders - Localized secure-element placeholders.
 * @param {Function} options.onCardBrandChange - Card-brand change handler.
 * @param {number} options.retryKey - Value incremented to retry initialization after an error.
 * @returns {object} Secure-element refs and lifecycle status.
 */
function useBasisTheoryElements({
  ariaLabels,
  publicKey,
  placeholders,
  onCardBrandChange,
  onCardDetailsChange,
  retryKey = 0,
}: UseBasisTheoryElementsOptions): UseBasisTheoryElementsResult {
  const clientRef = useRef<BasisTheoryClient | null>(null);
  const elementsRef = useRef<BasisTheoryCardElements | null>(null);
  const mountedRef = useRef(false);
  const [isComplete, setIsComplete] = useState(false);
  const [status, setStatus] = useState<BasisTheoryElementsStatus>('loading');
  const { cardNumber, expiration, cvc } = placeholders;
  const {
    cardNumber: cardNumberAriaLabel,
    expiration: expirationAriaLabel,
    cvc: cvcAriaLabel,
  } = ariaLabels;

  useEffect(() => {
    let cancelled = false;
    let mountsPending = false;
    let ownedElements: Partial<BasisTheoryCardElements> | null = null;
    let removeFocusBridges: Array<() => void> = [];
    let focusedSecureTargetId = '';
    const fieldCompletion = { cardNumber: false, expiration: false, cvc: false };
    let lastNotifiedCardDetails: BasisTheoryCardDetails = {
      bin: '',
      brand: '',
      expirationComplete: false,
      last4: '',
    };
    onCardBrandChange(lastNotifiedCardDetails.brand);
    onCardDetailsChange?.({ ...lastNotifiedCardDetails });
    /** Release every element owned by this effect. */
    function cleanupOwnedElements() {
      safeUnmountCardElements(ownedElements);
      ownedElements = null;
    }
    /** Resolve the client and mount each secure card element. */
    async function mountElements() {
      setIsComplete(false);
      setStatus('loading');
      const basisTheoryClient = await resolveBasisTheoryClient(publicKey);
      if (cancelled || mountedRef.current) return;

      ownedElements = {};
      const cardNumberElement = basisTheoryClient.createElement('cardNumber', {
        'targetId': 'bt-card-number',
        'aria-label': cardNumberAriaLabel,
        'placeholder': cardNumber,
        'iconPosition': 'right',
        'inputMode': 'numeric',
        'autoComplete': 'cc-number',
        'style': ELEMENT_STYLE,
      });
      ownedElements.cardNumber = cardNumberElement;
      const cardExpiration = basisTheoryClient.createElement('cardExpirationDate', {
        'targetId': 'bt-card-expiration',
        'aria-label': expirationAriaLabel,
        'placeholder': expiration,
        'inputMode': 'numeric',
        'autoComplete': 'cc-exp',
        'style': ELEMENT_STYLE,
      });
      ownedElements.cardExpiration = cardExpiration;
      const cardCvc = basisTheoryClient.createElement('cardVerificationCode', {
        'targetId': 'bt-card-cvc',
        'aria-label': cvcAriaLabel,
        'placeholder': cvc,
        'inputMode': 'numeric',
        'autoComplete': 'cc-csc',
        'style': ELEMENT_STYLE,
      });
      ownedElements.cardCvc = cardCvc;
      const cardElements: BasisTheoryCardElements = {
        cardNumber: cardNumberElement,
        cardExpiration,
        cardCvc,
      };

      const cardDetails: BasisTheoryCardDetails = { ...lastNotifiedCardDetails };

      function notifyCardDetails() {
        const nextCardDetails = { ...cardDetails };
        const detailsUnchanged =
          nextCardDetails.bin === lastNotifiedCardDetails.bin &&
          nextCardDetails.brand === lastNotifiedCardDetails.brand &&
          nextCardDetails.expirationComplete === lastNotifiedCardDetails.expirationComplete &&
          nextCardDetails.last4 === lastNotifiedCardDetails.last4;
        if (detailsUnchanged) return;

        if (nextCardDetails.brand !== lastNotifiedCardDetails.brand) {
          onCardBrandChange(nextCardDetails.brand);
        }
        lastNotifiedCardDetails = nextCardDetails;
        onCardDetailsChange?.(nextCardDetails);
      }

      function syncFieldCompletion(
        field: keyof typeof fieldCompletion,
        event: ElementValidityUpdate = {},
      ) {
        if (cancelled) return;
        fieldCompletion[field] = Boolean(event.complete && event.valid && !event.empty);
        setIsComplete(Object.values(fieldCompletion).every(Boolean));
      }

      /** Preserve the last useful number metadata when a later secure-element event omits it. */
      function syncCardNumberDetails(event: CardNumberUpdate = {}) {
        if (cancelled) return;
        const metadata = cardNumberElement.cardMetadata;

        if (event.empty) {
          cardDetails.brand = '';
          cardDetails.bin = '';
          cardDetails.last4 = '';
        } else {
          const brand = event.cardBrand || metadata?.cardBrand;
          const eventBin = normalizeCardBin(event.cardBin);
          const bin = eventBin || normalizeCardBin(metadata?.cardBin);
          const last4 = String(event.cardLast4 || metadata?.cardLast4 || '')
            .replace(/\D/g, '')
            .slice(-4);

          if (brand) cardDetails.brand = brand;
          else if (Object.prototype.hasOwnProperty.call(event, 'cardBrand')) cardDetails.brand = '';
          if (event.complete === false && !eventBin) cardDetails.bin = '';
          else if (bin) cardDetails.bin = bin;
          if (last4) cardDetails.last4 = last4;
          else if (
            event.complete === false ||
            event.cardBin ||
            Object.prototype.hasOwnProperty.call(event, 'cardLast4')
          ) {
            cardDetails.last4 = '';
          }
        }

        notifyCardDetails();
      }

      function syncExpirationDetails(event: ElementValidityUpdate = {}) {
        if (cancelled) return;
        if (event.complete !== undefined || event.valid !== undefined) {
          cardDetails.expirationComplete = Boolean(event.complete && event.valid);
        }
        syncCardNumberDetails();
      }

      function syncCvcDetails() {
        if (cancelled) return;
        // Keep this late metadata read for browser autofill, but notify React only when
        // the number or expiration snapshot actually changed.
        syncCardNumberDetails();
      }

      cardNumberElement.on('change', (event) => {
        syncFieldCompletion('cardNumber', event);
        syncCardNumberDetails(event);
      });
      cardNumberElement.on('focus', () => {
        focusedSecureTargetId = 'bt-card-number';
      });
      cardNumberElement.on('blur', (event) => {
        if (focusedSecureTargetId === 'bt-card-number') focusedSecureTargetId = '';
        syncFieldCompletion('cardNumber', event);
        syncCardNumberDetails(event);
      });
      cardExpiration.on('change', (event) => {
        syncFieldCompletion('expiration', event);
        syncExpirationDetails(event);
      });
      cardExpiration.on('focus', () => {
        focusedSecureTargetId = 'bt-card-expiration';
      });
      cardExpiration.on('blur', (event) => {
        if (focusedSecureTargetId === 'bt-card-expiration') focusedSecureTargetId = '';
        syncFieldCompletion('expiration', event);
        syncExpirationDetails(event);
      });
      cardCvc.on('change', (event) => {
        syncFieldCompletion('cvc', event);
        syncCvcDetails();
      });
      cardCvc.on('focus', () => {
        focusedSecureTargetId = 'bt-card-cvc';
      });
      cardCvc.on('blur', (event) => {
        if (focusedSecureTargetId === 'bt-card-cvc') focusedSecureTargetId = '';
        syncFieldCompletion('cvc', event);
        syncCvcDetails();
      });
      mountsPending = true;
      const mountResults = await Promise.allSettled([
        cardNumberElement.mount('#bt-card-number'),
        cardExpiration.mount('#bt-card-expiration'),
        cardCvc.mount('#bt-card-cvc'),
      ]);
      mountsPending = false;

      const failedMount = mountResults.find((result) => result.status === 'rejected');
      if (failedMount?.status === 'rejected') throw failedMount.reason;

      if (cancelled) {
        cleanupOwnedElements();
        return;
      }

      removeFocusBridges = [
        bridgeSecureElementFocus(
          'bt-card-number',
          cardNumberElement,
          () => focusedSecureTargetId === 'bt-card-number',
        ),
        bridgeSecureElementFocus(
          'bt-card-expiration',
          cardExpiration,
          () => focusedSecureTargetId === 'bt-card-expiration',
        ),
        bridgeSecureElementFocus(
          'bt-card-cvc',
          cardCvc,
          () => focusedSecureTargetId === 'bt-card-cvc',
        ),
      ];

      clientRef.current = basisTheoryClient;
      elementsRef.current = cardElements;
      mountedRef.current = true;
      setStatus('ready');
    }

    mountElements().catch((error) => {
      mountsPending = false;
      cleanupOwnedElements();
      if (cancelled) return;
      errorMonitoring.notify({ error, info: { stage: 'new_card_mount' } });
      setStatus('error');
    });

    return () => {
      cancelled = true;
      removeFocusBridges.forEach((removeBridge) => removeBridge());
      removeFocusBridges = [];
      safeUnmountCardElements(ownedElements);
      if (!mountsPending) ownedElements = null;
      clientRef.current = null;
      elementsRef.current = null;
      mountedRef.current = false;
    };
  }, [
    cardNumber,
    cardNumberAriaLabel,
    cvc,
    cvcAriaLabel,
    expiration,
    expirationAriaLabel,
    onCardBrandChange,
    onCardDetailsChange,
    publicKey,
    retryKey,
  ]);

  return { clientRef, elementsRef, isComplete, mountedRef, status };
}

export type { BasisTheoryCardDetails, BasisTheoryCardElements, BasisTheoryElementsStatus };
export { useBasisTheoryElements };
